import { z } from 'zod';

import { idSchema, isoDateTimeSchema, paginationSchema } from './common.js';

/**
 * Personas do formulário principal (copy Home > CTA FINAL).
 */
export const personaSchema = z.enum([
  'prefeito',
  'secretario',
  'equipe-tecnica',
  'investidor-operador',
  'imprensa',
  'outro',
]);
export type Persona = z.infer<typeof personaSchema>;

export const PERSONA_LABELS: Record<Persona, string> = {
  prefeito: 'Prefeito(a)',
  secretario: 'Secretário(a) municipal/estadual',
  'equipe-tecnica': 'Equipe técnica de governo',
  'investidor-operador': 'Investidor / operador privado',
  imprensa: 'Imprensa',
  outro: 'Outro',
};

/**
 * Tipos de lead que a tabela `leads` armazena. Espelha o enum Postgres
 * (`lead_tipo`).
 */
export const tipoLeadSchema = z.enum(['contato', 'newsletter', 'integridade', 'sugestao_pauta']);
export type TipoLead = z.infer<typeof tipoLeadSchema>;

/** Rótulos pt-BR de cada tipo de lead, para exibição no admin. */
export const TIPO_LEAD_LABELS: Record<TipoLead, string> = {
  contato: 'Contato',
  newsletter: 'Newsletter',
  integridade: 'Integridade',
  sugestao_pauta: 'Sugestão de pauta',
};

/**
 * Telefone brasileiro: 10 ou 11 dígitos (DDD + número), só números aceitos.
 * Backend faz strip de máscara antes de validar.
 */
export const phoneBrSchema = z
  .string()
  .regex(/^\d{10,11}$/, { message: 'Informe o telefone com DDD. Apenas números.' });

/**
 * Honeypot anti-bot, compartilhado pelos 3 formulários públicos.
 *
 * `website` é renderizado escondido e fora da ordem de tabulação: um
 * humano nunca o preenche, um bot que preenche todos os inputs sim. O
 * schema aceita qualquer string (a rejeição é decidida no handler, que
 * loga a tentativa) — validar aqui devolveria ao bot a dica de qual
 * campo o denunciou.
 *
 * Substituiu o Cloudflare Turnstile em 21/07/2026: o widget atrapalhava
 * usuários legítimos. A segunda barreira é o rate limit por IP na API
 * (`lib/rateLimit.ts`).
 */
export const honeypotShape = {
  website: z.string().max(200).optional().or(z.literal('')),
};

export const contactFormSchema = z.object({
  nome: z.string().min(3, { message: 'Informe seu nome completo.' }).max(120),
  email: z.string().email({ message: 'Verifique o formato do e-mail.' }),
  telefone: phoneBrSchema.optional().or(z.literal('')),
  persona: personaSchema,
  organizacao: z.string().min(1).max(200),
  mensagem: z.string().max(3000).optional().or(z.literal('')),
  lgpdConsent: z.literal(true, {
    errorMap: () => ({
      message: 'É necessário autorizar o tratamento dos dados para enviar a mensagem.',
    }),
  }),
  ...honeypotShape,
});
export type ContactFormInput = z.infer<typeof contactFormSchema>;

/**
 * Canal de integridade. Por design, **não exige nome nem e-mail**: o
 * relator pode optar por anonimato total. `lgpdConsent` é opcional (sem
 * dados pessoais coletados, LGPD não exige consent). O server **não**
 * captura IP nem User-Agent neste endpoint para preservar sigilo.
 */
export const integridadeFormSchema = z.object({
  /** Identificação opcional (relator pode permanecer anônimo). */
  nomeOpcional: z.string().max(120).optional().or(z.literal('')),
  emailOpcional: z
    .string()
    .email({ message: 'Verifique o formato do e-mail.' })
    .optional()
    .or(z.literal('')),
  /** Categoria sugerida pelo relator (livre, pra facilitar triagem interna). */
  categoria: z.enum(['conduta', 'conflito-interesse', 'fraude', 'assedio', 'outro']),
  /** Relato livre. */
  relato: z
    .string()
    .min(20, { message: 'Descreva o relato com pelo menos 20 caracteres.' })
    .max(5000),
  ...honeypotShape,
});
export type IntegridadeFormInput = z.infer<typeof integridadeFormSchema>;

/**
 * Sugestão de pauta editorial. E-mail opcional (mas se informado,
 * vira contato para follow-up). LGPD consent só obrigatório quando o
 * usuário escolhe receber resposta.
 */
export const sugestaoPautaFormSchema = z.object({
  nomeOpcional: z.string().max(120).optional().or(z.literal('')),
  emailOpcional: z
    .string()
    .email({ message: 'Verifique o formato do e-mail.' })
    .optional()
    .or(z.literal('')),
  titulo: z.string().min(5, { message: 'Dê um título claro à sugestão.' }).max(280),
  detalhes: z
    .string()
    .min(20, { message: 'Descreva a sugestão com pelo menos 20 caracteres.' })
    .max(3000),
  ...honeypotShape,
});
export type SugestaoPautaFormInput = z.infer<typeof sugestaoPautaFormSchema>;

/**
 * Resposta padronizada dos endpoints POST de lead. Não devolve o
 * payload submetido — só id + flag de e-mail enviado, para o front
 * mostrar success state apropriado.
 */
export const leadResponseSchema = z.object({
  id: idSchema,
  tipo: tipoLeadSchema,
  emailSent: z.boolean(),
  criadoEm: isoDateTimeSchema,
});
export type LeadResponse = z.infer<typeof leadResponseSchema>;

/* -------------------------------------------------------------------------- */
/*  Leitura no admin (Mensagens)                                              */
/* -------------------------------------------------------------------------- */

/**
 * Registro de lead como devolvido ao painel administrativo (somente
 * leitura). `data` é o payload jsonb cru do formulário — varia por
 * `tipo`, então fica como record genérico e o admin renderiza os campos
 * conhecidos com rótulos. Não há status/atendimento por design.
 */
export const leadRecordSchema = z.object({
  id: idSchema,
  tipo: tipoLeadSchema,
  data: z.record(z.string(), z.unknown()),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  criadoEm: isoDateTimeSchema,
});
export type LeadRecord = z.infer<typeof leadRecordSchema>;

/** Campos de ordenação aceitos pela listagem de leads. */
export const leadSortFieldSchema = z.enum(['criadoEm', 'tipo']);

/**
 * Query da listagem `GET /leads`. Paginação + filtro por tipo + busca
 * textual livre (case-insensitive sobre o payload) + ordenação.
 */
export const listLeadsQuerySchema = paginationSchema.extend({
  tipo: tipoLeadSchema.optional(),
  q: z.string().trim().max(120).optional(),
  sort: z.string().optional(),
});
export type ListLeadsQuery = z.infer<typeof listLeadsQuerySchema>;
